<template>
  <div class="pricing-dialog-backdrop">
    <div class="pricing-dialog">
      <h2 v-if="id">Update package</h2>
      <h2 v-else>Create a new package</h2>
      <div class="edit-form">
        <label>Name
        <tooltip>
          Understand the needs of your attendees and match them to the goals of a sponsor.
          For Ex: Bronze, Silver, Gold, Platinum, and Diamond.
        </tooltip>
      </label>
        <input type="text" v-model="title"
          class="package-title" maxlength="50"/>
      </div>
      <div class="edit-form">
        <label>Service
        <tooltip>
          The only difference between in-kind sponsorship and cash sponsorship
          is the form in which the sponsor contributes value to the event. We recommend
          aligning your values, do not be afraid to suggest collaborations. (Learn More)
        </tooltip>
      </label>
        <div class="service-container">
          <toggle-input
            :value="amount >= 0"
            @input="toggleServiceType"
            offText="In Kind"
            onText="Amount">
          </toggle-input>
          <div v-if="amount >= 0" class="max-amount-container">
            <input type="number" v-model="amount" placeholder="Dollars"
              class="package-amount"/>
            <input type="checkbox" :checked="isRangeAmount" id="max-amount-checkbox"
              @input="toggleRangeAmount" class="package-amount"/>
            <label for="max-amount-checkbox">Range</label>
            <input type="number" v-model="maxAmount" placeholder="Max Dollars"
              v-if="isRangeAmount" class="package-amount"/>
          </div>
        </div>
      </div>
      <div class="edit-form edit-form-margin-top">
        <label>Quantity
        <tooltip>
          Sponsors love category exclusivity. A one-time offer could be the key to
          sealing the deal. You must walk carefully between price and quantity, ensuring
          that your offer is of the quality your sponsors deserve. (Learn More)
        </tooltip>
      </label>
        <div class="quantity-container">
          <toggle-input
            :value="quantity >= 0"
            @input="v => v ? quantity = '' : quantity = -1"
            offText="Unlimited"
            onText="Limited">
          </toggle-input>
          <input type="number" v-model="quantity" placeholder="How Many"
            class="package-quantity" v-if="quantity >= 0"/>
        </div>
      </div>
      <div class="edit-form edit-form-margin-top">
        <label>Negotiable
        <tooltip>
          Tell your sponsor if your price and quantity is negotiable or fixed
        </tooltip>
      </label>
        <div class="negotiable-container">
          <toggle-input
            :value="negotiable"
            @input="negotiable = !negotiable"
            offText="Fixed"
            onText="Negotiable">
          </toggle-input>
        </div>
      </div>
      <div class="edit-form edit-form-margin-top">
        <label>Deadline
        <tooltip>
          Is there a deadline to when this item can be reserved?
        </tooltip>
      </label>
        <div class="deadline-container">
          <date-time-picker
            v-model="deadline"
            :options="{minDate: 'today', enableTime: true}"
            placeholder="Deadline">
          </date-time-picker>
        </div>
      </div>
      <div class="edit-form edit-form-margin-top">
        <label>What we will be offering
        <tooltip>
        Be specific about what you can offer to a partner. Sponsors want more than
        media exposure. They want creative ways to grow relationships with their
        current and prospective customers. (Learn More)
        </tooltip>
      </label>
        <v-select multiple taggable :options="allOfferings" placeholder="Customize your offerings"
          v-model="offerings" class="offer-select">
        </v-select>
      </div>
      <div class="edit-form edit-form-margin-top">
        <label>What we promise to deliver
        <tooltip>
        Think about ways you can create an ongoing connection between the brand and the supporter.
        You're a facilitator. Be reasonable. Delivering what you promise will impact
        personal credibility. And bonuses are given to who over deliver. (Learn More)
        </tooltip>
      </label>
        <div v-for="(deliverable, j) in deliverables" :key="j" class="deliverable-container">
          <input class="deliverable-input"
            placeholder="Ex: 10000 Unique Impressions" type="text"
            v-model="deliverables[j]">
          <font-awesome-icon :icon="['fa', 'minus-square']" size="1x"
            class="delete-deliverable" @click="deliverables.splice(j, 1)">
          </font-awesome-icon>
        </div>
        <button aria-label="Add Deliverable" class="add-link-button"
          @click="addDeliverable()"
          :disabled="deliverables.length >= 3">
          + Add Deliverable
        </button>
      </div>
      <div class="edit-form">
        <label>Description
        <tooltip>
          Provide relevant information about your event. Explain in a few words what
          benefits you can offer to this Package. Give high value exposure and prominent
          recognition for your sponsor.
          (Learn More)
        </tooltip>
      </label>
        <textarea type="text" v-model="description"
          rows="6" maxlength="500">
        </textarea>
        <span class="remaining-characters">{{500 - description.length}}/500</span>
      </div>
      <div class="edit-form">
        <div class="button-bar">
          <button class="button cancel-package cancel" aria-label="Cancel"
            @click="$emit('cancel')">
            Cancel
          </button>
          <button class="button save-package" aria-label="Save"
          @click="savePackage">
            <span v-if="id">Update Package</span>
            <span v-else>Create Package</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DateTimePicker from '@/components/DateTimePicker.vue';
import ToggleInput from '@/components/ToggleInput.vue';

export default {
  name: 'event-pricing-package-editable',
  components: {
    DateTimePicker,
    ToggleInput,
  },
  data() {
    return {
      id: '',
      title: '',
      amount: '',
      maxAmount: -1,
      quantity: '',
      negotiable: false,
      description: '',
      deadline: '',
      offerings: [],
      deliverables: [],
    };
  },
  props: [
    'eventId', 'allOfferings', 'pricingPackage',
  ],
  mounted() {
    document.body.style.overflow = 'hidden';
    if (this.pricingPackage !== 'new') {
      this.id = this.pricingPackage.id;
      this.title = this.pricingPackage.title;
      this.amount = this.pricingPackage.amount;
      this.maxAmount = this.pricingPackage.maxAmount;
      this.quantity = this.pricingPackage.quantity;
      this.negotiable = this.pricingPackage.negotiable;
      this.description = this.pricingPackage.description;
      this.deadline = this.pricingPackage.deadline;
      this.offerings = this.pricingPackage.offerings;
      this.deliverables = this.pricingPackage.deliverables;
    }
  },
  destroyed() {
    document.body.style.overflow = 'auto';
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    isRangeAmount() {
      return this.amount >= 0 && this.maxAmount >= 0;
    },
  },
  methods: {
    addDeliverable() {
      this.deliverables.push('');
    },
    toggleRangeAmount(event) {
      if (event.target.checked) {
        this.maxAmount = '';
      } else {
        this.maxAmount = -1;
      }
    },
    toggleServiceType(val) {
      this.maxAmount = -1;
      if (val) {
        this.amount = '';
      } else {
        this.amount = -1;
      }
    },
    async savePackage() {
      const newPackage = {
        title: this.title,
        amount: this.amount,
        maxAmount: this.maxAmount,
        quantity: this.quantity,
        negotiable: this.negotiable,
        deadline: this.deadline,
        description: this.description,
        offerings: this.offerings,
        deliverables: this.deliverables,
      };
      try {
        if (this.id) {
          // update
          await this.backendAxios
            .put(
              `/sponsorship-event/my-event/id/${this.eventId}/pricing-package/${this.pricingPackage.id}`,
              newPackage,
            );
        } else {
          // new
          await this.backendAxios
            .post(`/sponsorship-event/my-event/id/${this.eventId}/pricing-package`, newPackage);
        }
        this.$emit('save');
      } catch (err) {
        this.$toasted.show(err.response.data, {
          type: 'info',
          duration: 5000,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.pricing-dialog-backdrop {
  max-height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  transition: all 0.3s;
  z-index: 100;
}

.pricing-dialog {
  text-align: center;
  height: 90%;
  width: 100%;
  margin: auto;
  max-width: 50rem;
  -webkit-font-smoothing: subpixel-antialiased;
  max-height: 100%;
  overflow: scroll;
  background-color: white;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.3);
  padding: 1rem;
}

.service-container {
  width: 100%;
  position: relative;
}

.max-amount-container > input[type=checkbox] {
  width: 1rem;
  vertical-align: middle;
}

.package-title {
  margin-bottom: 1rem;
}

.deliverable-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
}

.deliverable-input {
  margin-bottom: 0.5rem;
}

.quantity-container, .service-container {
  width: 100%;
  display: flex;
  align-items: center;
}

.package-quantity, .package-amount {
  width: 8rem;
  margin-left: 1rem;
}

.negotiable-container {
  margin-right: auto;
}

.delete-deliverable {
  color: rgb(214, 14, 14);
  cursor: pointer;
  margin-left: 0.5rem;
}

.offer-select {
  width: 100%;
  background-color: white;
}

.deadline-container {
  width: 100%;
}

.button-bar {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.save-package {
  position: static;
}

.cancel-package {
  position: static;
  margin-right: 1rem;
}


@media screen and (max-width: 823px) {
  .pricing-dialog {
    width: 90%;
  }
}

@media screen and (min-height: 500px) {
  .pricing-dialog-backdrop {
    display: flex;
  }

  .pricing-dialog {
    align-self: center;
  }
}

</style>
